import axios, { AxiosInstance, AxiosError } from "axios";

// In production, API calls go to the same origin (empty string)
// In development, VITE_API_URL can be set to proxy to the backend
const API_BASE_URL = import.meta.env.VITE_API_URL || "";

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem("jwt_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Handle common errors
        if (error.response?.status === 401) {
          // Handle unauthorized
          localStorage.removeItem("jwt_token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  public get<T>(url: string, params?: Record<string, unknown>) {
    return this.client.get<T>(url, { params });
  }

  public post<T, D = unknown>(url: string, data?: D) {
    return this.client.post<T>(url, data);
  }

  public put<T, D = unknown>(url: string, data?: D) {
    return this.client.put<T>(url, data);
  }

  public patch<T, D = unknown>(url: string, data?: D) {
    return this.client.patch<T>(url, data);
  }

  public delete<T>(url: string) {
    return this.client.delete<T>(url);
  }
}

export const apiClient = new ApiClient();
